// owned by WP-TODAY · the rules of the ECONIC landing (`/`) that are not drawing (MoET review, 16 Oct 2026). Pure; tested in landing.test.mjs.
// 1. The six output prompts under the chat box: what ECONIC prepares ("Create an economic study on …", "Draft a policy on …", "Draft a
//    ministerial decision on …", "Review a law or regulation on …", "Model scenarios for …", "Prepare a Country360 briefing on …"). A prompt
//    puts its stem in the box, the caret at the end, and sends nothing; the person completes the subject. When the question goes, a text
//    that still starts with a stem carries that rung's server directive (`next study|policy|decision|review|scenarios|c360brief`, NextStepsLadder), so
//    the right flow runs: the study with the plan and the team, the policy on the federal template, the ministerial decision in the
//    Ministry's format, the review on Legal Affairs' checklist, the scenarios for the UAE, and the Minister's Country360 briefing
//    (`next c360brief`) through Country360's own brief pipeline (c360.brief.draft: the same template and tables as Country360's Briefs),
//    with the model or without it.
//    The briefing reads what Country360 reads (the Knowledge Bank and the Open Web by default since 21 Oct 2026; the Country Report itself
//    prints only the ICP Dataset unless estimates are included) while the box's sources row is at ECONIC's default; a row the person changed is their choice for it (handOffSources). A
//    question in Country360's own words goes to Country360 with its choice, as on its page, so it reads the same and answers the same
//    wherever it is asked (handOffOf).
// 2. The landing's two folded sections (Agents, My workspace): collapsed until the person opens one, the choice kept in this browser.
// 3. ⌘J on the landing (LANDING_NEW_EVENT): the page itself is the ECONIC chat (no column on "/"), so a new conversation is its empty box.

export type OutputKey = "study" | "policy" | "decision" | "review" | "scenarios" | "briefing";

export interface OutputPrompt {
  key: OutputKey;
  /** The directive the question carries (NextStepsLadder kinds). */
  directive: string | null;
  /** It writes a draft: not offered to a view-only account (the server's Drafting kinds). */
  writes: boolean;
}

/** In the order the Ministry gave them (16 Oct 2026). */
export const OUTPUTS: readonly OutputPrompt[] = [
  { key: "study", directive: "next study", writes: false },
  { key: "policy", directive: "next policy", writes: true },
  { key: "decision", directive: "next decision", writes: true },
  { key: "review", directive: "next review", writes: true },
  { key: "scenarios", directive: "next scenarios", writes: false },
  { key: "briefing", directive: "next c360brief", writes: true },
];

/** The agent whose colour each output wears in Signature and Aurora (24 Oct 2026, "more color throughout"): a study is the Economist's, a
 *  policy Policy (PMO)'s, a law's review the Librarian's (the laws are in the Knowledge Bank), scenarios the Modeller's, a briefing the
 *  Country Analyst's, a ministerial decision ECONIC's own. */
export const OUTPUT_AGENT: Readonly<Record<OutputKey, string>> = {
  study: "uae-radar", policy: "policylab", decision: "econic", review: "kb", scenarios: "outlook", briefing: "country360",
};

/** A prompt with its stem in the language on screen ("Create an economic study on "). */
export interface Stem { key: OutputKey; stem: string }

/** The prompts an account is offered: a view-only account reads, it does not draft. */
export function outputsFor(viewOnly: boolean): OutputPrompt[] {
  return OUTPUTS.filter((o) => !viewOnly || !o.writes);
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The stem as a pattern: its words in order, any run of spaces between them, any case, ending on a word ("… on" is not "… online"). */
function headOf(stem: string): RegExp | null {
  const words = stem.trim().split(/\s+/).filter(Boolean);
  return words.length ? new RegExp(`^\\s*${words.map(escape).join("\\s+")}(?=\\s|$)`, "iu") : null;
}

/** The stem the text starts with and the subject after it, or null. The longest stem first: one stem may begin another. */
export function stemOf(text: string, stems: readonly Stem[]): { key: OutputKey; subject: string } | null {
  for (const s of [...stems].sort((a, b) => b.stem.length - a.stem.length)) {
    const m = headOf(s.stem)?.exec(text);
    if (m) return { key: s.key, subject: text.slice(m[0].length).trim() };
  }
  return null;
}

/**
 * The box after a prompt is chosen: the stem, then what the box already said — the subject of another stem (choosing "Draft a policy on …"
 * after "Create an economic study on SME financing" keeps "SME financing"), or the words typed. The caret goes to the end.
 */
export function withStem(current: string, stem: string, stems: readonly Stem[]): string {
  const had = stemOf(current, stems);
  const subject = had ? had.subject : current.trim();
  return subject ? `${stem}${subject}` : stem;
}

/** Only a stem, nothing added yet: there is no question to send. */
export function isBareStem(text: string, stems: readonly Stem[]): boolean {
  const s = stemOf(text, stems);
  return !!s && !s.subject;
}

/** The directive a question carries: the rung of the stem it starts with, when it has a subject; else none. */
export function directiveFor(text: string, stems: readonly Stem[]): string | null {
  const s = stemOf(text, stems);
  if (!s || !s.subject) return null;
  return OUTPUTS.find((o) => o.key === s.key)?.directive ?? null;
}

/**
 * The sources a question from the box hands over for its conversation (`/agent?sources=`), when not the row's own: the Minister's Country360
 * briefing reads Country360's choice (the Knowledge Bank and the Open Web by default, 21 Oct 2026) while ECONIC's row is
 * at its default; a row the person changed decides. Null: the row's choice (the conversation reads ECONIC's sources).
 */
export function handOffSources(text: string, stems: readonly Stem[], row: { isDefault: boolean }, country360: readonly string[]): string | null {
  const s = stemOf(text, stems);
  if (!s?.subject || s.key !== "briefing" || !row.isDefault) return null;
  return country360.filter((k) => k === "kb" || k === "open").join(",") || "kb,open";
}

/** Where the country goes in one of Country360's own prompts as the landing reads it (`t(key, { country: SLOT })`). */
export const SLOT = "";

/** One of Country360's own prompts (agent/prompts.ts, Q23) as a pattern: the words before the country, and after it. */
export interface AgentStem { head: string; tail: string; slot: boolean }

/** The prompt's sentence, translated with SLOT for its country ("What changed with  this quarter?"), as a pattern. */
export function agentStemOf(template: string): AgentStem {
  const at = template.indexOf(SLOT);
  return at < 0 ? { head: template, tail: "", slot: false } : { head: template.slice(0, at), tail: template.slice(at + SLOT.length), slot: true };
}

const END = /[\s?!.؟،,]+$/u;
const norm = (s: string) => s.replace(END, "").replace(/\s+/g, " ").trim().toLowerCase();

/** Whether the text is one of those prompts with a country in place (any case, any run of spaces, the closing mark optional). */
export function matchesAgentStem(text: string, stems: readonly AgentStem[]): boolean {
  return stems.some((s) => {
    if (!s.slot) return !!norm(s.head) && norm(text) === norm(s.head);
    const words = s.head.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return false;
    // "… between the UAE and " ends on a word; the Arabic "… بين الدولة و" runs on into the country's name.
    const m = new RegExp(`^\\s*${words.map(escape).join("\\s+")}${/\s$/u.test(s.head) ? "(?:\\s+|$)" : ""}`, "iu").exec(text);
    if (!m) return false;
    const rest = norm(text.slice(m[0].length));
    const tail = norm(s.tail);
    if (!tail) return !!rest;
    return rest.endsWith(tail) && !!rest.slice(0, rest.length - tail.length).trim();
  });
}

/**
 * Where a question from the box goes and what it reads, when not ECONIC with the row's own choice (MoET review 16 Oct 2026, "same request,
 * same sources"): the Minister's Country360 briefing reads Country360's choice (handOffSources); a question in Country360's own words
 * ("Screen the bilateral economic activity between the UAE and India", "What changed with India this quarter?") goes to Country360 with
 * its choice, as on its page — both only while ECONIC's row is at its default. Null: ECONIC, the row's sources.
 */
export function handOffOf(text: string, stems: readonly Stem[], row: { isDefault: boolean }, country360: readonly string[], c360Stems: readonly AgentStem[]): { sources: string; agent: string | null } | null {
  const briefing = handOffSources(text, stems, row, country360);
  if (briefing) return { sources: briefing, agent: null };
  if (!row.isDefault || !matchesAgentStem(text.trim(), c360Stems)) return null;
  return { sources: country360.filter((k) => k === "kb" || k === "open").join(",") || "kb,open", agent: "country360" };
}

/**
 * Files dropped on the box, split by what the composer takes (its `accept` list, ".pdf,.docx,…" or MIME types such as "image/*"), as the
 * + picker does: a file of another kind is never handed to the new conversation, where it would fail only after the thread is created.
 */
export function acceptedFiles<F extends { name: string; type?: string }>(files: readonly F[], accept: string): { kept: F[]; skipped: F[] } {
  const rules = accept.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean);
  const ok = (f: F) => {
    const name = f.name.toLowerCase();
    const type = (f.type ?? "").toLowerCase();
    return rules.some((r) => (r.startsWith(".") ? name.endsWith(r) : r.endsWith("/*") ? type.startsWith(r.slice(0, -1)) : type === r));
  };
  return { kept: files.filter(ok), skipped: files.filter((f) => !ok(f)) };
}

/** ⌘J on "/" (the shell dispatches it): the box is emptied and takes the focus, a new ECONIC conversation (there is no column here). */
export const LANDING_NEW_EVENT = "econic:landing:new";

// ---------------------------------------------------------------- the folded sections

export type FoldKey = "agents" | "workspace";
export type Folds = Record<FoldKey, boolean>;

export const FOLDS_KEY = "econic.landing.folds";
/** Both collapsed until the person opens one. */
export const CLOSED: Folds = { agents: false, workspace: false };

/** The open sections as this browser remembers them (anything unreadable: both collapsed). */
export function parseFolds(raw: string | null | undefined): Folds {
  if (!raw) return { ...CLOSED };
  try {
    const v = JSON.parse(raw) as Partial<Record<FoldKey, unknown>> | null;
    if (!v || typeof v !== "object") return { ...CLOSED };
    return { agents: v.agents === true, workspace: v.workspace === true };
  } catch {
    return { ...CLOSED };
  }
}

export function serialiseFolds(folds: Folds): string {
  return JSON.stringify({ agents: folds.agents, workspace: folds.workspace });
}
